import { HOCKEY_TRADING_COMPETITION_CODE, canonicalCompetitionCode } from './competitionIdentity';

export type NhlConferenceFilter = 'ALL' | 'EAST' | 'WEST';
export type NhlDivisionFilter = 'ALL' | 'ATLANTIC' | 'METROPOLITAN' | 'CENTRAL' | 'PACIFIC';

type StructureTeam = {
  conference_code: string | null;
  division_code: string | null;
};

const DIVISIONS: Readonly<Record<Exclude<NhlConferenceFilter, 'ALL'>, readonly Exclude<NhlDivisionFilter, 'ALL'>[]>> = {
  EAST: ['ATLANTIC', 'METROPOLITAN'],
  WEST: ['CENTRAL', 'PACIFIC'],
};

function normalized(value: string | null): string {
  return (value ?? '').trim().toUpperCase();
}

export function isNhlRegularSeasonMarket(competitionCode: string | null | undefined, phase: string | null | undefined): boolean {
  return canonicalCompetitionCode(competitionCode) === HOCKEY_TRADING_COMPETITION_CODE && phase === 'REGULAR_SEASON';
}

export function nhlStructureOptions(teams: readonly StructureTeam[], conference: NhlConferenceFilter): {
  conferences: Exclude<NhlConferenceFilter, 'ALL'>[];
  divisions: Exclude<NhlDivisionFilter, 'ALL'>[];
} {
  const conferences = (['EAST', 'WEST'] as const).filter((code) => teams.some((team) => normalized(team.conference_code) === code));
  const divisions = (conference === 'ALL' ? [...DIVISIONS.EAST, ...DIVISIONS.WEST] : DIVISIONS[conference])
    .filter((code) => teams.some((team) => (
      normalized(team.division_code) === code
      && normalized(team.conference_code) === (DIVISIONS.EAST.includes(code) ? 'EAST' : 'WEST')
    )));
  return { conferences, divisions };
}

export function nextNhlConferenceSelection(conference: NhlConferenceFilter, division: NhlDivisionFilter): {
  conference: NhlConferenceFilter;
  division: NhlDivisionFilter;
} {
  return {
    conference,
    division: conference === 'ALL' || division === 'ALL' || DIVISIONS[conference].includes(division as Exclude<NhlDivisionFilter, 'ALL'>)
      ? division : 'ALL',
  };
}

export function matchesNhlStructure(team: StructureTeam, conference: NhlConferenceFilter, division: NhlDivisionFilter): boolean {
  return (conference === 'ALL' || normalized(team.conference_code) === conference)
    && (division === 'ALL' || normalized(team.division_code) === division);
}

export function filterNhlPlayoffSections<T extends StructureTeam, S extends {
  topLevelCode: string;
  kind: string;
  data: T[];
  showCutLineAfter: boolean;
}>(sections: readonly S[], conference: NhlConferenceFilter, division: NhlDivisionFilter): S[] {
  const visible = sections
    .map((section) => ({ ...section, data: section.data.filter((team) => matchesNhlStructure(team, conference, division)) }))
    .filter((section) => section.data.length > 0);
  return visible.map((section) => ({
    ...section,
    showCutLineAfter: section.showCutLineAfter && visible.some((other) => other.topLevelCode === section.topLevelCode && other.kind === 'OUTSIDE'),
  })) as S[];
}
